<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <div class="mb-4">
      <div class="flex items-center gap-2 mb-1">
        <div class="w-8 h-1 bg-blue-600 rounded-full"></div>
        <span class="text-[10px] font-black text-blue-600 tracking-widest">Recarga de Gas</span>
      </div>
      <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white flex items-center gap-2">
        <Icon icon="mdi:plus-circle" class="text-blue-600" /> Nuevo Pedido de Recarga
      </h1>
    </div>

    <!-- Pasos -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border p-4 mb-4 dark:border-gray-700">
      <div class="flex items-center justify-between">
        <div v-for="(s, i) in ['Cliente', 'Productos', 'Confirmar']" :key="i" class="flex items-center flex-1">
          <div :class="['w-10 h-10 rounded-full flex items-center justify-center font-black text-sm transition-all', pasoActual > i ? 'bg-emerald-500 text-white' : pasoActual === i ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500']">
            <Icon v-if="pasoActual > i" icon="mdi:check" />
            <span v-else>{{ i + 1 }}</span>
          </div>
          <span :class="['ml-2 text-xs font-black hidden sm:inline', pasoActual >= i ? 'text-gray-900 dark:text-white' : 'text-gray-400']">{{ s }}</span>
          <div v-if="i < 2" :class="['flex-1 h-0.5 mx-2', pasoActual > i ? 'bg-emerald-500' : 'bg-gray-200 dark:bg-gray-700']"></div>
        </div>
      </div>
    </div>

    <!-- Paso 1: Cliente -->
    <div v-if="pasoActual === 0" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border p-6 dark:border-gray-700">
      <h2 class="text-lg font-black text-gray-900 dark:text-white mb-4">Seleccionar Cliente</h2>
      <div class="relative mb-4">
        <Icon icon="mdi:magnify" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
        <input v-model="busquedaCliente" placeholder="Buscar por cédula, nombre o teléfono..." class="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 rounded-xl text-sm dark:text-white outline-none focus:border-blue-500" />
      </div>
      <div class="space-y-2 max-h-[400px] overflow-y-auto">
        <div v-for="c in clientesFiltrados" :key="c.idClienteGas" @click="seleccionarCliente(c)" :class="['p-4 rounded-xl border-2 cursor-pointer transition-all', clienteSeleccionado?.idClienteGas === c.idClienteGas ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-100 dark:border-gray-700 hover:border-blue-300']">
          <div class="flex items-center justify-between">
            <div>
              <p class="font-black text-sm text-gray-900 dark:text-white">{{ c.nombre }} {{ c.apellido }}</p>
              <p class="text-xs text-gray-500">{{ c.cedula }} · {{ c.telefono }}</p>
            </div>
            <Icon v-if="clienteSeleccionado?.idClienteGas === c.idClienteGas" icon="mdi:check-circle" class="text-blue-600 text-xl" />
          </div>
        </div>
        <p v-if="clientesFiltrados.length === 0" class="text-center py-8 text-gray-400">No hay clientes que coincidan</p>
      </div>
    </div>

    <!-- Paso 2: Productos -->
    <div v-if="pasoActual === 1" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border p-6 dark:border-gray-700">
      <h2 class="text-lg font-black text-gray-900 dark:text-white mb-4">Agregar Recargas</h2>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        <div v-for="t in tiposRecarga" :key="t.idTipoRecarga" @click="agregarItem(t)" class="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-xl p-4 border-2 border-orange-100 dark:border-orange-800 cursor-pointer hover:shadow-md transition-all">
          <div class="flex items-center justify-between mb-2">
            <Icon icon="mdi:fire" class="text-orange-600 text-xl" />
            <span class="text-xs font-black text-orange-600">{{ t.codigo }}</span>
          </div>
          <p class="font-black text-sm text-gray-900 dark:text-white">{{ t.nombre }}</p>
          <div class="flex justify-between mt-2 text-xs">
            <span class="text-gray-500">{{ t.kilogramos }} kg · {{ t.litrosEquivalente }} L</span>
            <span class="font-black text-emerald-600">${{ Number(t.precioUsd).toFixed(2) }}</span>
          </div>
        </div>
      </div>

      <h3 class="text-sm font-black text-gray-700 dark:text-white mb-3">Items agregados</h3>
      <div class="space-y-3">
        <div v-for="(item, index) in items" :key="index" class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border dark:border-gray-700">
          <div class="flex items-center justify-between mb-3">
            <p class="font-black text-sm text-gray-900 dark:text-white">{{ item.nombre }}</p>
            <button @click="items.splice(index, 1)" class="p-1 bg-rose-100 text-rose-600 rounded-lg"><Icon icon="mdi:close" /></button>
          </div>
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label class="block text-[9px] font-black text-gray-400 uppercase mb-1">Cantidad</label>
              <input v-model.number="item.cantidad" type="number" min="1" class="w-full px-3 py-2 bg-white dark:bg-gray-800 border rounded-lg text-xs dark:text-white" />
            </div>
            <div>
              <label class="block text-[9px] font-black text-gray-400 uppercase mb-1">Kilos</label>
              <input v-model.number="item.kilogramos" type="number" readonly class="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 border rounded-lg text-xs dark:text-white" />
            </div>
            <div>
              <label class="block text-[9px] font-black text-gray-400 uppercase mb-1">Litros (editable)</label>
              <input v-model.number="item.litros" type="number" step="0.1" class="w-full px-3 py-2 bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-200 dark:border-blue-800 rounded-lg text-xs dark:text-white" />
            </div>
            <div>
              <label class="block text-[9px] font-black text-gray-400 uppercase mb-1">Precio USD</label>
              <input v-model.number="item.precioUnitarioUsd" type="number" step="0.01" class="w-full px-3 py-2 bg-white dark:bg-gray-800 border rounded-lg text-xs dark:text-white" />
            </div>
          </div>
          <div class="text-right mt-3 pt-3 border-t dark:border-gray-700">
            <span class="text-xs text-gray-500">Subtotal: </span>
            <span class="font-black text-emerald-600">${{ (item.precioUnitarioUsd * item.cantidad).toFixed(2) }}</span>
          </div>
        </div>
        <p v-if="items.length === 0" class="text-center py-8 text-gray-400 text-sm">Agrega al menos un producto</p>
      </div>
    </div>

    <!-- Paso 3: Confirmar -->
    <div v-if="pasoActual === 2" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border p-6 dark:border-gray-700 space-y-5">
      <h2 class="text-lg font-black text-gray-900 dark:text-white">Confirmar Pedido</h2>

      <!-- Dirección -->
      <div class="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
        <label class="block text-[10px] font-black text-blue-600 tracking-widest mb-2">Dirección de Entrega *</label>
        <textarea v-model="pedido.direccionEntrega" rows="2" class="w-full px-4 py-2 bg-white dark:bg-gray-800 border-2 rounded-xl text-xs dark:text-white mb-2"></textarea>
        <label class="block text-[10px] font-black text-blue-600 tracking-widest mb-2">URL del Mapa *</label>
        <input v-model="pedido.urlMapaEntrega" placeholder="https://maps.google.com/..." class="w-full px-4 py-2 bg-white dark:bg-gray-800 border-2 rounded-xl text-xs dark:text-white" />
      </div>

      <!-- Zona -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Zona</label>
          <select v-model="pedido.idZona" @change="aplicarCostoDelivery" class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white">
            <option :value="null">Sin zona</option>
            <option v-for="z in zonas" :key="z.idZona" :value="z.idZona">{{ z.nombre }} (${{ z.costoDelivery }})</option>
          </select>
        </div>
        <div>
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Tipo de Pago</label>
          <select v-model="pedido.tipoPago" class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white">
            <option value="contado">Contado</option>
            <option value="credito">Crédito</option>
          </select>
        </div>
      </div>

      <!-- Tasa -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Tasa Bs/USD *</label>
          <input v-model.number="pedido.tasaBsUsd" type="number" step="0.01" class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white" />
        </div>
        <div>
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">IVA %</label>
          <input v-model.number="pedido.ivaPorcentaje" type="number" step="0.01" class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white" />
        </div>
      </div>

      <!-- Totales -->
      <div class="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 rounded-xl p-4 border border-emerald-200 dark:border-emerald-800">
        <h3 class="text-xs font-black text-emerald-700 dark:text-emerald-400 uppercase tracking-widest mb-3">Resumen</h3>
        <div class="space-y-1 text-sm">
          <div class="flex justify-between"><span class="text-gray-600 dark:text-gray-400">Subtotal:</span><span class="font-black dark:text-white">${{ subtotal.toFixed(2) }}</span></div>
          <div class="flex justify-between"><span class="text-gray-600 dark:text-gray-400">Delivery:</span><span class="font-black dark:text-white">${{ costoDelivery.toFixed(2) }}</span></div>
          <div class="flex justify-between"><span class="text-gray-600 dark:text-gray-400">IVA ({{ pedido.ivaPorcentaje }}%):</span><span class="font-black dark:text-white">${{ iva.toFixed(2) }}</span></div>
          <div class="flex justify-between pt-2 border-t border-emerald-200 dark:border-emerald-800">
            <span class="font-black text-emerald-700 dark:text-emerald-400">TOTAL USD:</span>
            <span class="font-black text-emerald-700 dark:text-emerald-400 text-lg">${{ total.toFixed(2) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="font-bold text-gray-600 dark:text-gray-400">TOTAL Bs:</span>
            <span class="font-black text-blue-600 dark:text-blue-400">Bs. {{ (total * pedido.tasaBsUsd).toFixed(2) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Navegación -->
    <div class="flex gap-3 mt-4">
      <button v-if="pasoActual > 0" @click="pasoActual--" class="px-6 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl font-black text-xs dark:text-gray-300">
        ← Anterior
      </button>
      <button v-if="pasoActual < 2" @click="siguientePaso" :disabled="!puedeAvanzar" class="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-black text-xs uppercase disabled:opacity-50">
        Siguiente →
      </button>
      <button v-if="pasoActual === 2" @click="crearPedido" :disabled="saving" class="flex-1 px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl font-black text-xs uppercase disabled:opacity-50 flex items-center justify-center gap-2">
        <Icon :icon="saving ? 'mdi:loading' : 'mdi:check'" :class="{'animate-spin': saving}" />
        {{ saving ? 'Creando...' : 'Crear Pedido' }}
      </button>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { obtenerClientesGas, obtenerTiposRecarga, obtenerZonas, crearPedidoRecarga } from '@/api/gas.js';

export default {
  name: 'NuevoPedidoRecarga',
  components: { Icon },
  data() {
    return {
      pasoActual: 0,
      clientes: [], tiposRecarga: [], zonas: [],
      busquedaCliente: '',
      clienteSeleccionado: null,
      items: [],
      pedido: {
        direccionEntrega: '', urlMapaEntrega: '', idZona: null, tipoPago: 'contado',
        tasaBsUsd: 1, ivaPorcentaje: 16, observaciones: '', canalOrigen: 'presencial',
        descuentoUsd: 0
      },
      saving: false
    };
  },
  computed: {
    clientesFiltrados() {
      if (!this.busquedaCliente.trim()) return this.clientes;
      const q = this.busquedaCliente.toLowerCase();
      return this.clientes.filter(c => (c.nombre || '').toLowerCase().includes(q) || (c.apellido || '').toLowerCase().includes(q) || (c.cedula || '').toLowerCase().includes(q) || (c.telefono || '').includes(q));
    },
    subtotal() { return this.items.reduce((s, i) => s + (i.precioUnitarioUsd * i.cantidad), 0); },
    costoDelivery() {
      const z = this.zonas.find(x => x.idZona === this.pedido.idZona);
      return z ? Number(z.costoDelivery || 0) : 0;
    },
    iva() { return (this.subtotal - this.pedido.descuentoUsd) * (this.pedido.ivaPorcentaje / 100); },
    total() { return this.subtotal + this.costoDelivery + this.iva - this.pedido.descuentoUsd; },
    puedeAvanzar() {
      if (this.pasoActual === 0) return !!this.clienteSeleccionado;
      if (this.pasoActual === 1) return this.items.length > 0;
      return true;
    }
  },
  async mounted() {
    const [c, t, z] = await Promise.all([obtenerClientesGas('activos'), obtenerTiposRecarga('activos'), obtenerZonas('activas')]);
    if (c.success) this.clientes = c.data;
    if (t.success) this.tiposRecarga = t.data;
    if (z.success) this.zonas = z.data;
  },
  methods: {
    seleccionarCliente(c) {
      this.clienteSeleccionado = c;
      this.pedido.direccionEntrega = c.direccion;
      this.pedido.urlMapaEntrega = c.urlMapa;
      this.pedido.idZona = c.idZona;
    },
    agregarItem(t) {
      this.items.push({
        idTipoRecarga: t.idTipoRecarga,
        nombre: t.nombre,
        cantidad: 1,
        kilogramos: Number(t.kilogramos),
        litros: Number(t.litrosEquivalente),
        precioUnitarioUsd: Number(t.precioUsd),
        descuentoPorcentaje: 0
      });
    },
    siguientePaso() { if (this.puedeAvanzar) this.pasoActual++; },
    aplicarCostoDelivery() { /* ya se calcula en computed */ },
    async crearPedido() {
      if (!this.pedido.urlMapaEntrega.trim()) { alert('La URL del mapa es obligatoria'); return; }
      if (!this.pedido.tasaBsUsd || this.pedido.tasaBsUsd <= 0) { alert('La tasa debe ser mayor a 0'); return; }

      this.saving = true;
      try {
        const payload = {
          idClienteGas: this.clienteSeleccionado.idClienteGas,
          idZona: this.pedido.idZona,
          canalOrigen: this.pedido.canalOrigen,
          direccionEntrega: this.pedido.direccionEntrega,
          urlMapaEntrega: this.pedido.urlMapaEntrega,
          telefonoContacto: this.clienteSeleccionado.telefono,
          nombreRecibe: `${this.clienteSeleccionado.nombre} ${this.clienteSeleccionado.apellido}`,
          detalles: this.items.map(i => ({
            idTipoRecarga: i.idTipoRecarga,
            cantidad: i.cantidad,
            litros: i.litros,
            precioUnitarioUsd: i.precioUnitarioUsd,
            descuentoPorcentaje: i.descuentoPorcentaje
          })),
          descuentoUsd: this.pedido.descuentoUsd,
          ivaPorcentaje: this.pedido.ivaPorcentaje,
          tasaBsUsd: this.pedido.tasaBsUsd,
          tipoPago: this.pedido.tipoPago,
          observaciones: this.pedido.observaciones
        };

        const res = await crearPedidoRecarga(payload);
        if (res.success) {
          alert(`Pedido ${res.data.numeroPedido || ''} creado exitosamente`);
          this.$router.push('/gas/pedidos');
        } else {
          alert(res.error || 'Error al crear el pedido');
        }
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
</style>